import React, { useRef, KeyboardEvent } from 'react';
import { useTabsContext } from './Tabs';
import { StyledTabList } from './Tabs.styles';

export interface TabListProps {
  children: React.ReactNode;
  className?: string;
}

const TabList: React.FC<TabListProps> = ({ children, className }) => {
  const context = useTabsContext();

  if (!context) {
    throw new Error('TabList must be used within a Tabs component');
  }

  const { orientation, variant } = context;
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    const isHorizontal = orientation === 'horizontal';

    if (
      (isHorizontal && (key === 'ArrowLeft' || key === 'ArrowRight')) ||
      (!isHorizontal && (key === 'ArrowUp' || key === 'ArrowDown'))
    ) {
      event.preventDefault();

      const tabs = tabListRef.current?.querySelectorAll('[role="tab"]:not([aria-disabled="true"])');
      if (!tabs) return;

      const currentIndex = Array.from(tabs).findIndex(tab => tab === document.activeElement);
      let nextIndex = currentIndex;

      if ((isHorizontal && key === 'ArrowRight') || (!isHorizontal && key === 'ArrowDown')) {
        nextIndex = (currentIndex + 1) % tabs.length;
      } else if ((isHorizontal && key === 'ArrowLeft') || (!isHorizontal && key === 'ArrowUp')) {
        nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
      }

      const nextTab = tabs[nextIndex] as HTMLElement;
      nextTab?.focus();
      nextTab?.click();
    }
  };

  return (
    <StyledTabList
      ref={tabListRef}
      role="tablist"
      aria-orientation={orientation}
      onKeyDown={handleKeyDown}
      className={className}
      orientation={orientation}
      variant={variant}
    >
      {children}
    </StyledTabList>
  );
};

export default TabList;